import React, { useState } from 'react';
import { Checkbox } from '@/hybrid/core/Checkbox.atom';
import { bindingsDefinition, injected, prop } from '@/hybrid/core/bindings.util';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TextButton } from '@/hybrid/core/TextButton.atom';
import { HoverTooltip } from '@/hybrid/core/HoverTooltip.atom';
import TimeZoneSelector from '@/hybrid/core/TimeZoneSelector.molecule';
import { useInjectedBindings } from '@/hybrid/core/hooks/useInjectedBindings.hook';
import { WorkbenchActions } from '@/workbench/workbench.actions';
import { WorksheetActions } from '@/worksheet/worksheet.actions';
import _ from 'lodash';
import { sqWorksheetStore } from '@/core/core.stores';

const timezoneModalBindings = bindingsDefinition({
  close: prop<() => void>(),
  userFallback: prop<boolean>(),
  onChange: prop.optional<() => void>(),
  sqWorkbenchActions: injected<WorkbenchActions>(),
  sqWorksheetActions: injected<WorksheetActions>(),
});

export const TimezoneModal: SeeqComponent<typeof timezoneModalBindings> = ({
  close,
  onChange = _.noop,
  userFallback,
}) => {
  const { t } = useTranslation();

  const { sqWorkbenchActions, sqWorksheetActions } = useInjectedBindings(timezoneModalBindings);

  const [fixed, setFixed] = useState(sqWorksheetStore.timezoneFixed);
  const [timezone, setTimezone] = useState(sqWorksheetStore.timezone);

  const openUserProfile = () => {
    sqWorkbenchActions.setUserProfileDisplay(true);
    close();
  };

  const save = () => {
    if (fixed !== sqWorksheetStore.timezoneFixed || timezone !== sqWorksheetStore.timezone) {
      sqWorksheetActions.setTimezone(fixed ? timezone : undefined);
      onChange();
    }
    close();
  };

  return (
    <Modal show={true} onHide={close} animation={false} size="sm">
      <Modal.Header closeButton={true} onHide={close}>
        <Modal.Title>{t('TIME_ZONE_MODAL.TITLE')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb15">
          {t(userFallback ? 'TIME_ZONE_MODAL.DESCRIPTION_WORKSHEET' : 'TIME_ZONE_MODAL.DESCRIPTION_TOPIC')}
        </div>
        <div className="flexRowContainer mb0">
          <div id="timezoneLocal" className="flexColumnContainer flexSpaceBetween flexAlignCenter">
            <Checkbox
              type="radio"
              id="setTimeZoneFlex"
              isChecked={!fixed}
              onClick={() => setFixed(false)}
              tooltip={userFallback ? 'TIME_ZONE_MODAL.USER_TOOLTIP' : 'TIME_ZONE_MODAL.NOT_CONFIGURED_TOOLTIP'}
              label={userFallback ? 'TIME_ZONE_MODAL.USER' : 'TIME_ZONE_MODAL.NOT_CONFIGURED'}
            />
            {userFallback && (
              <HoverTooltip text="TIME_ZONE_MODAL.EDIT_USER_TOOLTIP" placement="right">
                <a
                  type="button"
                  className="ml5 mr8 cursorPointer font-size-smaller text-italic sq-text-primary text-underline"
                  onClick={openUserProfile}>
                  {t('TIME_ZONE_MODAL.EDIT_USER')}
                </a>
              </HoverTooltip>
            )}
          </div>
          <div id="timezoneWorksheet" className="flexRowContainer mt2" onClick={() => setFixed(true)}>
            <Checkbox
              type="radio"
              id="setTimeZoneFixed"
              isChecked={fixed}
              onClick={() => setFixed(true)}
              tooltip={userFallback ? 'TIME_ZONE_MODAL.FIXED_TOOLTIP' : 'TIME_ZONE_MODAL.FIXED_TOPIC_TOOLTIP'}
              label="TIME_ZONE_MODAL.FIXED"
            />
            <HoverTooltip
              placement="right"
              text={userFallback ? 'TIME_ZONE_MODAL.FIXED_TOOLTIP' : 'TIME_ZONE_MODAL.FIXED_TOPIC_TOOLTIP'}>
              <div className="ml25">
                <TimeZoneSelector timezone={timezone} onSelect={setTimezone} />
              </div>
            </HoverTooltip>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flexFill" />
        <TextButton extraClassNames="mr15 min-width-60" label="CANCEL" onClick={close} testId="cancelButton" />
        <TextButton extraClassNames="min-width-60" label="SAVE" onClick={save} testId="saveButton" variant="theme" />
      </Modal.Footer>
    </Modal>
  );
};
